/* The effects of sins — what the Qurʾān and the Sunnah say disobedience does, and the way out.
   Every hadith is quoted from the Sunan collections with the scholars' grading (Virtues.tsx);
   the āyāt use the muṣḥaf's own text in the reader's chosen script. Content: scripts/fetch-virtues.mjs. */
import { ChevronRight, DoorOpen, Earth, HandHeart, HeartCrack, Scale, Wheat, type LucideIcon } from 'lucide-react';
import { useSearchParams } from 'react-router';
import { Card, LargeTitle } from '@/components/ui';
import { AdhkarArabic, AdhkarDisplayButton } from './AdhkarText';
import { AYAT, HadithCard, SINS, VIRTUES_CREDIT, type SinSection } from './Virtues';

const refOf = (keys: string[]) => {
  const [s, a] = keys[0].split(':');
  return keys.length === 1 ? `Qurʾān ${keys[0]}` : `Qurʾān ${s}:${a}–${keys[keys.length - 1].split(':')[1]}`;
};

export function AyatCard({ keys, lead }: { keys: string[]; lead?: string }) {
  return (
    <article className="rounded-2xl bg-surface p-4 shadow-[inset_0_0_0_1px_var(--card-edge)]" data-ayat={keys.join(',')}>
      <div className="pb-2 text-[13px] font-semibold text-gold">{refOf(keys)}{lead ? ` · ${lead}` : ''}</div>
      <AdhkarArabic ar="" quran={keys} base={24} />
      <p className="mt-3 text-[15px] leading-relaxed">{keys.map((k) => AYAT[k]).join(' ')}</p>
    </article>
  );
}

function SectionBlock({ s }: { s: SinSection }) {
  return (
    <section id={s.id} className="px-4 pb-7">
      <div className="flex items-baseline justify-between gap-3 px-1">
        <h2 className="text-[19px] font-bold">{s.title}</h2>
        <span className="font-kufi text-[18px] text-gold" dir="rtl">{s.ar}</span>
      </div>
      <p className="px-1 pb-3 pt-1 text-[14px] leading-snug text-muted">{s.intro}</p>
      <div className="space-y-3">
        {s.items.map((it, i) => ('h' in it ? <HadithCard key={it.h} id={it.h} /> : <AyatCard key={`${it.ayat[0]}-${i}`} keys={it.ayat} lead={it.lead} />))}
      </div>
    </section>
  );
}

/** One icon per section: tap it to read that section. */
export const SIN_ICONS: Record<string, { icon: LucideIcon; short: string; colour: string }> = {
  heart: { icon: HeartCrack, short: 'The heart', colour: 'var(--win-crit)' },
  provision: { icon: Wheat, short: 'Provision', colour: 'var(--gold)' },
  dua: { icon: HandHeart, short: 'Duʿāʾ & worship', colour: 'var(--accent)' },
  world: { icon: Earth, short: 'The world', colour: '#3b82c4' },
  hereafter: { icon: Scale, short: 'Judgement Day', colour: '#8a63c9' },
  'way-out': { icon: DoorOpen, short: 'The way out', colour: 'var(--win-ok)' },
};

export function SinsPage() {
  const [params, setParams] = useSearchParams();
  const open = SINS.sections.find((s) => s.id === params.get('s')) ?? null;
  const pick = (id: string) => setParams(open?.id === id ? {} : { s: id }, { replace: false });
  return (
    <div className="page-pad">
      <LargeTitle back="/adhkar" title="Effects of sins" ar="آثار الذنوب" subtitle="And the way out" right={<AdhkarDisplayButton />} />
      <div className="px-4 pb-6">
        <Card className="p-5"><p className="text-[15px] leading-relaxed">{SINS.intro}</p></Card>
      </div>
      <div className="grid grid-cols-3 gap-3 px-4 pb-6" role="tablist" aria-label="Effects of sins">
        {SINS.sections.map((s) => {
          const { icon: Icon, short, colour } = SIN_ICONS[s.id];
          const on = open?.id === s.id;
          return (
            <button key={s.id} type="button" role="tab" aria-selected={on} aria-label={s.title} onClick={() => pick(s.id)}
              className={`press flex flex-col items-center gap-2 rounded-[20px] p-3 text-center transition-colors ${on ? 'bg-surface ring-2' : 'bg-surface/70'}`}
              style={on ? { boxShadow: `0 0 0 2px ${colour}` } : undefined}>
              <span className="grid h-14 w-14 place-items-center rounded-full" style={{ color: colour, background: `color-mix(in srgb, ${colour} 15%, transparent)` }}><Icon size={28} /></span>
              <span className="text-[13px] font-semibold leading-tight">{short}</span>
              <span className="font-kufi text-[13px] leading-none text-gold" dir="rtl">{s.ar}</span>
            </button>
          );
        })}
      </div>
      {open ? <SectionBlock s={open} /> : <p className="px-6 pb-6 text-center text-[14px] text-muted">Tap an icon to read what sins do there.</p>}
      <div className="space-y-3 px-4 pb-6">
        <div className="px-1 text-[13px] font-semibold uppercase tracking-wide text-muted">Return to Him now</div>
        <Card to="/adhkar/dhikr/istighfar-tawbah" className="flex items-center gap-3">
          <span className="min-w-0 flex-1">
            <span className="block font-arabic text-[20px] leading-snug" dir="rtl" lang="ar">أَسْتَغْفِرُ اللَّهَ وَأَتُوبُ إِلَيْهِ</span>
            <span className="block text-[13px] text-muted">Astaghfirullāh wa atūbu ilayh — count it</span>
          </span>
          <ChevronRight size={18} className="shrink-0 text-faint" />
        </Card>
        <Card to="/adhkar/morning" className="flex items-center gap-3">
          <span className="min-w-0 flex-1">
            <span className="block text-[16px] font-semibold">Sayyid al-Istighfār</span>
            <span className="block text-[13px] text-muted">The master supplication for forgiveness — in the morning and evening adhkar</span>
          </span>
          <ChevronRight size={18} className="shrink-0 text-faint" />
        </Card>
      </div>
      <p className="px-6 pb-6 text-[12px] leading-snug text-muted">{VIRTUES_CREDIT} Gradings are the named scholars’ own. This is a reminder, not a substitute for learning from a qualified teacher.</p>
    </div>
  );
}
